// CartPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Typography,
  Box,
  Button,
  IconButton,
  Card,
  Grid,
  Divider,
  Stack,
  Avatar,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Paper,
  Backdrop,
  Snackbar
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useNavigate } from "react-router-dom";
import API from "../api/api";
import CouponSelector from "./CouponSelector";
// ✅ IMPORT ADDED HERE
import AddressDrawer from "../components/AddressDrawer";
import ErrorOverlay from "../components/ErrorOverlay";
import GlobalSkeleton from "../components/GlobalSkeleton";

export default function CartPage() {
  const navigate = useNavigate();
  // 1. CORE STATE: The Cart & Address
  const [cart, setCart] = useState([]);
  const [address, setAddress] = useState(null);
  // sk
  const placingRef = React.useRef(false);

  // 👇 ADD THESE TWO NEW STATES
  const [showLocationError, setShowLocationError] = useState(false);
  const [locationErrorText, setLocationErrorText] = useState("");

  // 2. UI STATE: Loading, Error, Placing Order
  const [isLoading, setIsLoading] = useState(true);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // 3. UI STATE: Minimizing Subtotal Bill and Bill Summary Dialog
  const [isSubtotalMinimized, setIsSubtotalMinimized] = useState(false);

  // ✅ NEW STATE: Address Confirmation Dialog & Drawer
  const [confirmAddressOpen, setConfirmAddressOpen] = useState(false);
  const [isAddressDrawerOpen, setIsAddressDrawerOpen] = useState(false); // ✅ STATE ADDED HERE

  // ✅ NEW STATE: Show success animation screen
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  // 👇 ADD THIS NEW STATE
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [showCouponSuccess, setShowCouponSuccess] = useState(false);

  const billingData = useMemo(() => {

    const itemTotal = cart.reduce(
      (sum, item) =>
        sum +
        ((item.display_price || item.selling_price) *
          item.quantity),
      0
    );

    const subtotal = cart.reduce(
      (sum, item) =>
        sum +
        (item.selling_price * item.quantity),
      0
    );

    const additionalDiscount =
      itemTotal - subtotal;

    // Coupon Discount
    const couponDiscountAmount =
      couponDiscount || 0;

    // After coupon
    const subtotalAfterCoupon =
      Math.max(
        subtotal - couponDiscountAmount,
        0
      );

    // 🚫 GST DISABLED — Mealno is not GST registered, so no GST is charged.
    // const gst =
    //   Math.round(
    //     subtotalAfterCoupon * 0.05 * 100
    //   ) / 100;
    const gst = 0;

    const totalToPay =
      Math.round(
        (subtotalAfterCoupon + gst) * 100
      ) / 100;

    return {
      itemTotal,
      subtotal,
      additionalDiscount,
      couponDiscountAmount,
      subtotalAfterCoupon,
      gst,
      totalToPay
    };

  }, [cart, couponDiscount]);

  useEffect(() => {
    if (!selectedCoupon) {
      setCouponDiscount(0);
      return;
    }

    // 1. Calculate new discount locally FIRST (NO API CALL REQUIRED!)
    let newDiscount = 0;
    if (selectedCoupon.discount_type === "PERCENTAGE") {
      newDiscount = (billingData.subtotal * selectedCoupon.discount_value) / 100;
      if (selectedCoupon.max_discount) {
        newDiscount = Math.min(newDiscount, selectedCoupon.max_discount);
      }
    } else {
      newDiscount = Math.min(selectedCoupon.discount_value, billingData.subtotal);
    }

    const roundedDiscount = Math.round(newDiscount);

    // 2. Strict Check: Ensure the subtotal AFTER discount is still above the minimum
    if ((billingData.subtotal - roundedDiscount) < selectedCoupon.minimum_order_amount) {
      const shortfall = selectedCoupon.minimum_order_amount - (billingData.subtotal - roundedDiscount);
      setSelectedCoupon(null);
      setCouponDiscount(0);
      localStorage.removeItem("selectedCoupon");
      localStorage.removeItem("couponDiscount");
      setAlertMessage(`Add ₹${Number(shortfall.toFixed(2))} more to keep ${selectedCoupon.code} applied`);
      setOpenSnackbar(true);
      return;
    }

    setCouponDiscount(roundedDiscount);

  }, [billingData.subtotal, selectedCoupon]);

  // ==========================================
  // ✅ PHASE 1: Loading & Cart Synchronization
  // ==========================================

  const validateCartItems = async (cartItems, checkAddress = null) => {
    try {
      const payload = {
        items: cartItems.map(item => ({
          menu_item_id: item.id,
          quantity: item.quantity
        }))
      };

      if (checkAddress) {
        payload.lat = checkAddress.lat;
        payload.lng = checkAddress.lng;
      }

      const res = await API.post("/validate-cart", payload);

      // 🛡️ FIX 1: Safely extract data to prevent "Cannot read properties of undefined"
      const responseData = res.data || {};
      const validItemsList = responseData.valid_items || [];

      const validIds = validItemsList.map(item => item.menu_item_id);
      const filteredCart = cartItems.filter(item => validIds.includes(item.id));

      if (filteredCart.length === 0) {
        localStorage.removeItem("mealnoCart");
        setCart([]);
        return false;
      }

      if (filteredCart.length !== cartItems.length) {
        localStorage.setItem("mealnoCart", JSON.stringify(filteredCart));
        setCart(filteredCart);
        return false;
      }

      setCart(cartItems);
      return true;

    } catch (err) {
      console.error("Cart validation failed", err);

      // 🛡️ FIX 2: Safely check if it's an HTTP error before reading the message
      if (err.response && err.response.data) {
        const backendError = err.response.data.detail || err.response.data.error;

        if (backendError) {
          const errorStr = backendError.toLowerCase();
          if (errorStr.includes("range") || errorStr.includes("zone") || errorStr.includes("bounds")) {
            setLocationErrorText(backendError);
            setShowLocationError(true);
          } else {
            setErrorMessage(backendError);
          }
          return false;
        }
      }

      // 🛡️ FIX 3: If it's a network drop or crash, don't blindly approve!
      setErrorMessage("Validation error. Please try again.");
      return false;
    }
  };

  useEffect(() => {

    const loadCart = async () => {

      const savedCart =
        localStorage.getItem("mealnoCart");

      const savedAddress =
        localStorage.getItem("selectedAddress");

      const savedCoupon =
        localStorage.getItem("selectedCoupon");

      const savedDiscount =
        localStorage.getItem("couponDiscount");

      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        const parsedAddress = savedAddress ? JSON.parse(savedAddress) : null;

        // ✅ Pass the address into the validation check
        await validateCartItems(parsedCart, parsedAddress);
      }

      if (savedAddress) {
        setAddress(JSON.parse(savedAddress));
      } else {
        setErrorMessage(
          "Please set a delivery address before proceeding."
        );
      }

      if (savedCoupon) {
        setSelectedCoupon(
          JSON.parse(savedCoupon)
        );
      }

      if (savedDiscount) {
        setCouponDiscount(
          Number(savedDiscount)
        );
      }

      setIsLoading(false);
    };

    loadCart();

  }, []);

  const syncCartToStorage = useCallback((updatedCart) => {
    localStorage.setItem("mealnoCart", JSON.stringify(updatedCart));
  }, []);

  const updateQuantity = (itemId, change) => {
    setCart((prev) => {
      const updatedCart = prev.map((item) => {
        if (item.id === itemId) {
          return { ...item, quantity: item.quantity + change };
        }
        return item;
      }).filter((item) => item.quantity > 0);

      syncCartToStorage(updatedCart);
      return updatedCart;
    });
  };

  // ==========================================
  // ✅ PHASE 3: Order Placement Logic
  // ==========================================
  const handlePlaceOrder = async () => {

    if (placingRef.current) return;
    placingRef.current = true;

    const savedUser = JSON.parse(localStorage.getItem("user"));

    if (!savedUser || !savedUser.id || !address?.id || cart.length === 0) {
      setErrorMessage("Validation failed. Check login, address, and cart.");
      placingRef.current = false;
      return;
    }

    setErrorMessage("");
    setPlacingOrder(true);

    const isCartStillValid = await validateCartItems(cart, address);

    if (!isCartStillValid) {
      setPlacingOrder(false);
      placingRef.current = false;
      setAlertMessage("An item became unavailable and was removed.");
      setOpenSnackbar(true);
      return;
    }

    const orderPayload = {
      lat: address.lat,
      lng: address.lng,
      coupon_code: selectedCoupon?.code || null,
      items: cart.map(item => ({
        menu_item_id: item.id,
        quantity: item.quantity,
        vendor_id: item.vendor_id
      }))
    };

    try {
      const res = await API.post("/order", orderPayload);
      const payload = res?.data?.data || res?.data || res || {};

      // 👇 FIX: Catch graceful backend errors returned as 200 OK
      if (payload.success === false) {
        setErrorMessage(payload.error || payload.detail || "Order validation failed.");
        setPlacingOrder(false);
        placingRef.current = false;
        return;
      }

      const orderId = payload.order_id || payload.id || payload.orderId;

      if (orderId) {
        localStorage.removeItem("mealnoCart");
        localStorage.removeItem("selectedCoupon");
        localStorage.removeItem("couponDiscount");
        window.dispatchEvent(new Event("storage"));

        const existingOrders = JSON.parse(localStorage.getItem("activeOrders")) || [];
        if (!existingOrders.includes(orderId)) {
          existingOrders.push(orderId);
        }
        localStorage.setItem("activeOrders", JSON.stringify(existingOrders));
        window.dispatchEvent(new Event("storage"));

        setShowSuccessScreen(true);
        setPlacingOrder(false);

        setTimeout(() => {
          setCart([]);
          setSelectedCoupon(null);
          setCouponDiscount(0);

          navigate(`/live-order/${orderId}`, {
            replace: true,
            state: {
              optimisticOrder: {
                order_id: orderId,
                status: "PLACED",
                final_amount: billingData.totalToPay,
                items: cart
              }
            }
          });
        }, 2500);

      } else {
        // Fallback for severe structural mismatches
        throw new Error("Server communication succeeded, but missing Order ID.");
      }

    } catch (err) {
      console.error("Order Error:", err);

      const detailStr = err.response?.data?.detail || err.response?.data?.error || err.message || "";

      // 👇 GRACEFULLY CATCH COUPON ERRORS SO IT DOESN'T BREAK THE CART UI
      if (typeof detailStr === 'string' && detailStr.toLowerCase().includes("coupon")) {
        setSelectedCoupon(null);
        setCouponDiscount(0);
        localStorage.removeItem("selectedCoupon");
        localStorage.removeItem("couponDiscount");
        setAlertMessage(detailStr); // Show the backend error in the red snackbar
        setOpenSnackbar(true);
        setPlacingOrder(false);
        placingRef.current = false;
        return; // Stop execution without triggering the full-screen Cart Issue
      }

      setErrorMessage(
        detailStr || "Error connecting to server."
      );

      setPlacingOrder(false);
      placingRef.current = false;
    }
  };

  // ==========================================
  // ✅ PHASE 4: Rendering UI States
  // ==========================================
  if (isLoading || cart.length === 0 || errorMessage) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#FFFDF8" }}>
        <Box sx={{ background: "#F8D548", borderRadius: "0 0 30px 30px", pb: 3, pt: 2, px: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ color: "#4A332E", bgcolor: "rgba(255,255,255,0.15)" }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" fontWeight="800" color="#4A332E">
              Cart Summary
            </Typography>
          </Stack>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 5, pt: 15, textAlign: 'center' }}>
          {isLoading ? (
            <Box sx={{ width: '100%', mt: -5 }}>
              {/* mt:-5 to offset the padding of the wrapper box */}
              <GlobalSkeleton type="list" />
            </Box>
          ) : errorMessage ? (
            <>
              <ErrorOutlineIcon sx={{ color: '#E85D3F', fontSize: 70, mb: 3 }} />
              <Typography variant="h6" fontWeight="800" sx={{ mb: 2, color: '#3A2C23' }}>Cart Issue</Typography>
              <Typography variant="body2" sx={{ color: '#6E655E', mb: 6, maxWidth: '280px' }}>{errorMessage}</Typography>
              {errorMessage.includes("address") && (
                <Button variant="outlined" onClick={() => navigate("/")} sx={{ color: '#F29D17', borderColor: '#F29D17', borderRadius: '16px', fontWeight: 'bold' }}>
                  Add Delivery Address
                </Button>
              )}
            </>
          ) : (
            <>
              <Avatar sx={{ bgcolor: '#F8D548', width: 90, height: 90, mb: 3, border: '4px solid #FFFFFF', boxShadow: '0 4px 16px rgba(58, 44, 35, 0.08)' }}>
                <LocationOnIcon sx={{ color: '#3A2C23', fontSize: 40 }} />
              </Avatar>
              <Typography variant="h6" fontWeight="800" sx={{ mb: 2, color: '#3A2C23' }}>Cart Empty</Typography>
              <Typography variant="body2" sx={{ color: '#6E655E', mb: 6, maxWidth: '280px' }}>Your Local Mealno Cart is empty. Add delicious homemade items to get started!</Typography>

              <Button
                variant="contained"
                disableElevation
                onClick={() => navigate("/menu")}
                sx={{
                  bgcolor: '#F8C332',
                  color: '#3A2C23',
                  borderRadius: '16px',
                  fontWeight: '800',
                  py: 1.5, px: 4,
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#F5B71D' }
                }}
              >
                View Menu Items
              </Button>
            </>
          )}
        </Box>
      </Box>
    );
  }

  // ==========================================
  // ✅ PHASE 5: Standard Cart View
  // ==========================================
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#FFFDF8", pb: 5 }}>

      <Box sx={{ background: "#F8D548", borderRadius: "0 0 30px 30px", pb: 3, pt: 2, px: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ color: "#4A332E", bgcolor: "rgba(255,255,255,0.15)" }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="h6" fontWeight="800" sx={{ lineHeight: 1.1, color: "#4A332E" }}>
              Cart Summary
            </Typography>
            <Typography variant="caption" sx={{ color: "#6E655E", fontWeight: 600 }}>
              {cart.length} {cart.length === 1 ? 'Item' : 'Items'} in cart
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ p: 2, pt: 0 }}>
        {cart.map((item) => (
          <Card key={item.id} sx={{
            mb: 2, p: 2,
            borderRadius: '24px',
            bgcolor: '#FFFFFF',
            border: '1px solid #ECE5DA',
            boxShadow: '0 4px 16px rgba(58, 44, 35, 0.04)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>

              <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                <Avatar
                  variant="rounded"
                  src={item.image}
                  sx={{ width: 70, height: 70, borderRadius: '16px', boxShadow: '0 4px 12px rgba(58,44,35,0.08)' }}
                />
                <Box sx={{ pr: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight="800" sx={{ color: "#2F2A25", lineHeight: 1.2, mb: 0.5 }} noWrap>{item.name}</Typography>

                  {/* 👇 UPDATED: Theme aligned Veg/Non-Veg */}
                  {(() => {
                    const isNonVeg = item.category && item.category.toLowerCase().includes('non');
                    const indicatorColor = isNonVeg ? '#E85D3F' : '#6A9E3A';

                    return (
                      <Stack direction="row" spacing={0.8} alignItems="center">
                        <Box
                          sx={{
                            width: 14, height: 14, border: `1.5px solid ${indicatorColor}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            borderRadius: '3px'
                          }}
                        >
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: indicatorColor }} />
                        </Box>
                        <Typography variant="caption" sx={{ color: "#6E655E", fontWeight: 700 }}>
                          {isNonVeg ? 'Non Veg' : 'Veg'}
                        </Typography>
                      </Stack>
                    );
                  })()}
                </Box>
              </Stack>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1.5 }}>
                  {item.display_price > item.selling_price && (
                    <Typography sx={{ textDecoration: "line-through", color: "#A59D96", fontSize: 12, fontWeight: 600 }}>
                      ₹{Math.round(item.display_price * item.quantity * 100) / 100}
                    </Typography>
                  )}
                  <Typography variant="subtitle1" fontWeight="800" sx={{ color: "#2F2A25" }}>
                    ₹{Math.round(item.selling_price * item.quantity * 100) / 100}
                  </Typography>
                </Box>

                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    border: '1px solid #ECE5DA',
                    borderRadius: '16px',
                    px: 0.5, py: 0.5,
                    bgcolor: '#FFFDF8',
                    minWidth: '85px'
                  }}
                >
                  <IconButton size="small" onClick={() => updateQuantity(item.id, -1)} sx={{ color: '#F29D17', p: 0.5 }}>
                    <Typography variant="body1" fontWeight="bold" sx={{ lineHeight: 1 }}>-</Typography>
                  </IconButton>

                  <Typography variant="body2" fontWeight="800" sx={{ color: "#2F2A25", minWidth: '16px', textAlign: 'center' }}>
                    {item.quantity}
                  </Typography>

                  <IconButton size="small" onClick={() => updateQuantity(item.id, 1)} sx={{ color: '#F29D17', p: 0.5 }}>
                    <Typography variant="body1" fontWeight="bold" sx={{ lineHeight: 1 }}>+</Typography>
                  </IconButton>
                </Stack>
              </Box>
            </Box>
          </Card>
        ))}

        <Button
          fullWidth
          sx={{
            color: '#F29D17',
            justifyContent: 'center',
            mb: 3, py: 1,
            textTransform: 'none',
            fontWeight: '700',
            border: '1px dashed #ECE5DA',
            borderRadius: '16px',
            bgcolor: '#FFFFFF'
          }}
          onClick={() => navigate("/menu")}
        >
          <AddCircleOutlineIcon sx={{ mr: 1, fontSize: 20 }} /> Add more items
        </Button>

        {address && (
          <Card sx={{
            p: 2, mb: 2,
            borderRadius: '24px',
            bgcolor: '#FFFFFF',
            border: '1px solid #ECE5DA',
            boxShadow: '0 4px 16px rgba(58, 44, 35, 0.04)'
          }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs="auto">
                <Avatar sx={{ bgcolor: '#F8D548', width: 44, height: 44, border: '2px solid #FFFFFF', boxShadow: '0 4px 10px rgba(58,44,35,0.05)' }}>
                  <LocationOnIcon sx={{ color: '#3A2C23', fontSize: 22 }} />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="subtitle2" sx={{ color: '#3A2C23', fontWeight: 800 }}>
                  Delivering to <Typography component="span" sx={{ color: '#F29D17', fontWeight: 900 }}> {address.address_type} </Typography>
                </Typography>
                <Typography variant="caption" sx={{ color: "#6E655E", display: 'block', mt: 0.5, fontWeight: 500 }} noWrap>
                  {address.flat}, {address.building}, {address.landmark}
                </Typography>
              </Grid>
            </Grid>
          </Card>
        )}

        <CouponSelector
          subtotal={billingData.subtotal}
          selectedCoupon={selectedCoupon}
          couponDiscount={couponDiscount}
          vendorId={cart.length > 0 ? cart[0].vendor_id : null}
          onCouponApplied={(coupon, discount) => {

            setSelectedCoupon(coupon);
            setCouponDiscount(discount);

            if (coupon) {
              setShowCouponSuccess(true);

              setTimeout(() => {
                setShowCouponSuccess(false);
              }, 3000);
              localStorage.setItem(
                "selectedCoupon",
                JSON.stringify(coupon)
              );

              localStorage.setItem(
                "couponDiscount",
                discount
              );

            } else {

              localStorage.removeItem(
                "selectedCoupon"
              );

              localStorage.removeItem(
                "couponDiscount"
              );
            }
          }}
        />

        <Card
          sx={{
            p: 2, mt: 2, borderRadius: '24px', cursor: 'pointer',
            bgcolor: '#FFFFFF',
            border: '1px solid #ECE5DA',
            boxShadow: '0 4px 16px rgba(58, 44, 35, 0.04)'
          }}
          onClick={() => setIsSubtotalMinimized(!isSubtotalMinimized)}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="caption" sx={{
                bgcolor: '#FFF6DE',
                color: '#F29D17',
                fontSize: '0.85rem',
                px: 1.5, py: 0.6, borderRadius: '10px', fontWeight: '800'
              }}>
                Bill Summary
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              {isSubtotalMinimized ? <KeyboardArrowDownIcon sx={{ color: '#A59D96' }} /> : <KeyboardArrowUpIcon sx={{ color: '#A59D96' }} />}
            </Stack>
          </Stack>

          {!isSubtotalMinimized && (
            <Box sx={{ mt: 3, mb: 1 }}>
              <Stack spacing={1.5}>
                <Box display="flex" justifyContent="space-between" sx={{ color: '#6E655E' }}>
                  <Typography variant="body2" fontWeight="500">Item total</Typography>
                  <Typography variant="body2" fontWeight="700" sx={{ color: "#2F2A25" }}>
                    ₹{billingData.itemTotal}
                  </Typography>
                </Box>

                {billingData.additionalDiscount > 0 && (
                  <Box display="flex" justifyContent="space-between" sx={{ color: "#6A9E3A" }}>
                    <Typography variant="body2" fontWeight="700">Additional Discount</Typography>
                    <Typography variant="body2" fontWeight="800">-₹{billingData.additionalDiscount}</Typography>
                  </Box>
                )}
                {billingData.couponDiscountAmount > 0 && (
                  <Box display="flex" justifyContent="space-between" sx={{ color: "#6A9E3A" }}>
                    <Typography variant="body2" fontWeight="700">Coupon Discount</Typography>
                    <Typography variant="body2" fontWeight="800">-₹{billingData.couponDiscountAmount}</Typography>
                  </Box>
                )}

                <Box display="flex" justifyContent="space-between" sx={{ color: '#6E655E' }}>
                  <Typography variant="body2" fontWeight="500">Delivery Charge</Typography>
                  <Typography variant="body2" fontWeight="800" sx={{ color: "#6A9E3A" }}>FREE</Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" sx={{ color: '#6E655E' }}>
                  <Typography variant="body2" fontWeight="500">Platform Fee</Typography>
                  <Typography variant="body2" fontWeight="800" sx={{ color: "#6A9E3A" }}>FREE</Typography>
                </Box>

                {/* 🚫 GST DISABLED — Mealno is not GST registered, so no GST is charged. */}
                {/*
                <Box display="flex" justifyContent="space-between" sx={{ color: '#6E655E' }}>
                  <Typography variant="body2" fontWeight="500">GST (5%)</Typography>
                  <Typography variant="body2" fontWeight="700" sx={{ color: "#2F2A25" }}>₹{billingData.gst.toFixed(2)}</Typography>
                </Box>
                */}

                <Divider sx={{ borderColor: '#ECE5DA', my: 1.5 }} />

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" fontWeight="800" sx={{ color: "#3A2C23" }}>Total to Pay</Typography>
                  <Typography variant="h6" fontWeight="900" sx={{ color: "#2F2A25" }}>₹{billingData.totalToPay}</Typography>
                </Box>
              </Stack>
            </Box>
          )}
        </Card>

        <Card
          sx={{
            p: 2, mt: 2, borderRadius: '20px',
            bgcolor: 'rgba(232, 93, 63, 0.05)',
            border: '1px solid rgba(232, 93, 63, 0.2)',
            boxShadow: 'none'
          }}
        >
          <Typography variant="body2" sx={{ color: '#E85D3F', lineHeight: 1.5, fontSize: '0.8rem' }}>
            <strong style={{ fontWeight: 800 }}>Cancellation Policy:</strong> Orders cannot be cancelled after confirmation. Please ensure all details are correct before placing.
          </Typography>
        </Card>
      </Box>

      {/* E. Fixed Bottom Action Bar */}
      {billingData.totalToPay > 0 && (
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            bottom: 0, left: 0, right: 0,
            p: 2, pb: 3,
            bgcolor: '#FFFFFF',
            borderRadius: '24px 24px 0 0',
            borderTop: '1px solid #ECE5DA',
            boxShadow: '0 -8px 24px rgba(58, 44, 35, 0.05)',
            zIndex: 1100
          }}
        >
          <Stack direction="row" spacing={3} alignItems="center" justifyContent="space-between">
            <Box sx={{ pl: 1 }}>
              <Typography variant="caption" sx={{ color: '#6E655E', fontWeight: '800', letterSpacing: '0.5px', display: 'block', mb: 0.2 }}>
                TOTAL TO PAY
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: '900', color: '#2F2A25', lineHeight: 1 }}>
                ₹{billingData.totalToPay}
              </Typography>
            </Box>

            <Button
              variant="contained"
              disableElevation
              onClick={() => {
                if (billingData.totalToPay < 50) {
                  const amountNeeded = (50 - billingData.totalToPay).toFixed(2).replace(/\.00$/, '');
                  setAlertMessage(`Add ₹${amountNeeded} more to place your order`);
                  setOpenSnackbar(true);
                  return;
                }
                setConfirmAddressOpen(true);
              }}
              disabled={placingOrder}
              sx={{
                flex: 1,
                maxWidth: '60%',
                bgcolor: '#F8C332',
                color: '#3A2C23',
                borderRadius: '16px',
                py: 1.5,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(248, 195, 50, 0.3)',
                transition: 'all 0.2s ease',
                '&:hover': { bgcolor: '#F5B71D' },
                '&:active': { transform: 'scale(0.96)' },

                ...(billingData.totalToPay < 50 && {
                  bgcolor: '#F2EEE8',
                  color: '#A59D96',
                  boxShadow: 'none',
                }),

                '&.Mui-disabled': {
                  bgcolor: '#F2EEE8',
                  color: '#A59D96 !important',
                  boxShadow: 'none',
                  opacity: 1
                }
              }}
            >
              {placingOrder ? (
                <CircularProgress size={24} sx={{ color: '#3A2C23' }} />
              ) : (
                <Stack direction="column" alignItems="center" spacing={0}>
                  <Typography variant="subtitle1" fontWeight="800" sx={{ lineHeight: 1.2 }}>
                    Place Order
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: '700', fontSize: '0.7rem' }}>
                    {billingData.totalToPay < 50
                      ? `Add ₹${(50 - billingData.totalToPay).toFixed(2).replace(/\.00$/, '')} more`
                      : "Cash on Delivery"}
                  </Typography>
                </Stack>
              )}
            </Button>
          </Stack>
        </Paper>
      )}

      <Dialog
        open={confirmAddressOpen}
        onClose={() => setConfirmAddressOpen(false)}
        PaperProps={{ sx: { borderRadius: '24px', p: 1, bgcolor: '#FFFFFF', border: '1px solid #ECE5DA' } }}
      >
        <DialogTitle sx={{ fontWeight: '900', textAlign: 'center', pb: 1, color: '#3A2C23' }}>
          Deliver to this address?
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ mb: 1, color: '#6E655E', fontWeight: 500 }}>
            {address ? `${address.flat}, ${address.building}, ${address.landmark || ''}` : "No address selected."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 2 }}>
          {/* ✅ UPDATED CHANGE BUTTON BEHAVIOR */}
          <Button
            variant="outlined"
            onClick={() => {
              setConfirmAddressOpen(false);
              setIsAddressDrawerOpen(true);
            }}
            sx={{ color: '#6E655E', borderColor: '#ECE5DA', borderRadius: '12px', px: 3, py: 1, fontWeight: 'bold', textTransform: 'none' }}
          >
            Change it
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => {
              setConfirmAddressOpen(false);
              handlePlaceOrder();
            }}
            sx={{ bgcolor: '#F8C332', color: '#3A2C23', borderRadius: '12px', px: 4, py: 1, fontWeight: 'bold', textTransform: 'none', '&:hover': { bgcolor: '#F5B71D' } }}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ ADDED ADDRESS DRAWER COMPONENT */}
      <AddressDrawer
        open={isAddressDrawerOpen}
        onClose={() => setIsAddressDrawerOpen(false)}
        onSelect={async (newAddr) => {
          setIsAddressDrawerOpen(false);

          // 1. Show the loading spinner while checking the new zone
          setIsLoading(true);

          // 2. Validate the current cart against the newly selected address coordinates
          const isCartStillValid = await validateCartItems(cart, newAddr);

          setIsLoading(false);

          // 3. Update the global address states
          setAddress(newAddr);
          localStorage.setItem("selectedAddress", JSON.stringify(newAddr));

          // 👇 NEW: ALWAYS CLEAR COUPON WHEN ADDRESS CHANGES
          setSelectedCoupon(null);
          setCouponDiscount(0);
          localStorage.removeItem("selectedCoupon");
          localStorage.removeItem("couponDiscount");

          // 4. Handle the UI based on validation results
          if (isCartStillValid) {
            // Cart is safe! Seamlessly open confirmation to proceed
            setConfirmAddressOpen(true);
          } else {
            // Cart items were removed. Show a snackbar warning.
            setAlertMessage("Some items are not deliverable to this new zone and were removed.");
            setOpenSnackbar(true);
          }
        }}
      />

      {/* ✅ PREMIUM: Google Pay Inspired Animated Success Screen */}
      <Backdrop
        open={showSuccessScreen}
        sx={{
          zIndex: 2000,
          flexDirection: 'column',
          bgcolor: '#FFFDF8',
        }}
      >
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', px: 3 }}>

          {/* Outer Ripple / Pulse Effect Container */}
          <Box sx={{ position: 'relative', width: 140, height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>

            {/* Radiating Radar Wave 1 */}
            <Box
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                border: '2px solid #6A9E3A',
                opacity: 0,
                animation: 'radarPulse 2s cubic-bezier(0.165, 0.84, 0.44, 1) infinite',
                '@keyframes radarPulse': {
                  '0%': { transform: 'scale(0.6)', opacity: 0.5 },
                  '100%': { transform: 'scale(1.4)', opacity: 0 }
                }
              }}
            />

            {/* Radiating Radar Wave 2 (Delayed) */}
            <Box
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                border: '2px solid #6A9E3A',
                opacity: 0,
                animation: 'radarPulse 2s cubic-bezier(0.165, 0.84, 0.44, 1) infinite',
                animationDelay: '0.6s'
              }}
            />

            {/* Central Success Circle */}
            <Box
              sx={{
                position: 'relative',
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: '#DDECC8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(106, 158, 58, 0.15)',
                animation: 'circlePopIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
                '@keyframes circlePopIn': {
                  '0%': { transform: 'scale(0)' },
                  '100%': { transform: 'scale(1)' }
                },
                // 👇 MOVED ANIMATION TO MUI SX PROP
                '& path': {
                  strokeDasharray: 40,
                  strokeDashoffset: 40,
                  animation: 'drawPremiumCheck 0.5s ease-out 0.4s forwards'
                },
                '@keyframes drawPremiumCheck': {
                  to: { strokeDashoffset: 0 }
                }
              }}
            >
              {/* Animated Self-Drawing Checkmark */}
              <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="#6A9E3A" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </Box>
          </Box>

          {/* Smooth Fade-In Header */}
          <Typography
            variant="h4"
            fontWeight="900"
            sx={{
              color: '#3A2C23',
              mb: 1.5,
              animation: 'textSlideUp 0.6s cubic-bezier(0.215, 0.61, 0.355, 1) 0.5s both',
              '@keyframes textSlideUp': {
                '0%': { opacity: 0, transform: 'translateY(20px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' }
              }
            }}
          >
            Order Placed!
          </Typography>

          {/* Smooth Fade-In Subtitle */}
          <Typography
            variant="subtitle1"
            sx={{
              color: '#6E655E',
              fontWeight: '600',
              animation: 'textSlideUp 0.6s cubic-bezier(0.215, 0.61, 0.355, 1) 0.65s both'
            }}
          >
            Preparing your delicious food...
          </Typography>
        </Box>
      </Backdrop>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          top: "75px !important", left: "50% !important", transform: "translateX(-50%) !important",
          width: "92%", maxWidth: "360px",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: "flex", alignItems: "center", width: "100%", py: 1.5, px: 2,
            borderRadius: "16px",
            bgcolor: "#E85D3F",
            border: "1px solid #c9462d",
            boxShadow: "0 4px 16px rgba(58, 44, 35, 0.08)",
          }}
        >
          <ErrorOutlineIcon sx={{ color: "#FFFDF8", fontSize: 20, mr: 1.5, flexShrink: 0 }} />
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#FFFDF8", lineHeight: 1.35 }}>
            {alertMessage || "Minimum order value is ₹50"}
          </Typography>
        </Paper>
      </Snackbar>

      <Backdrop
        open={showCouponSuccess}
        sx={{
          zIndex: 1800,
          backgroundColor: "rgba(0,0,0,0.15)",
          backdropFilter: "blur(3px)",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            px: 4,
            py: 4,
            borderRadius: "24px",
            bgcolor: "#fff",
            minWidth: 280,

            display: "flex",
            flexDirection: "column",
            alignItems: "center",

            boxShadow: "0 20px 50px rgba(0,0,0,0.15)",

            animation: "popupIn .35s ease",

            "@keyframes popupIn": {
              from: {
                opacity: 0,
                transform: "scale(.8)",
              },
              to: {
                opacity: 1,
                transform: "scale(1)",
              },
            },
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "#16A34A",

              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              animation: "popCircle .45s ease",

              "@keyframes popCircle": {
                "0%": { transform: "scale(0)" },
                "70%": { transform: "scale(1.15)" },
                "100%": { transform: "scale(1)" },
              },
              // 👇 MOVED ANIMATION TO MUI SX PROP
              "& path": {
                strokeDasharray: 40,
                strokeDashoffset: 40,
                animation: "drawCouponCheck .45s ease .25s forwards",
              },
              "@keyframes drawCouponCheck": {
                to: { strokeDashoffset: 0 }
              }
            }}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </Box>

          <Typography
            sx={{
              mt: 2.5,
              fontWeight: 800,
              fontSize: "1rem",
              color: "#111",
              textAlign: "center",
            }}
          >
            {selectedCoupon?.code} applied successfully
          </Typography>

          <Typography
            sx={{
              mt: 0.5,
              color: "#16A34A",
              fontWeight: 700,
              fontSize: "0.85rem",
              textAlign: "center",
            }}
          >
            You saved ₹{couponDiscount} on this order 🎉
          </Typography>
        </Paper>
      </Backdrop>

      {/* ✅ NEW: Animated Location Error Screen */}
      <ErrorOverlay
        open={showLocationError}
        message={locationErrorText}
        onClose={() => setShowLocationError(false)}
        onChangeAddress={() => {
          setShowLocationError(false);
          setIsAddressDrawerOpen(true);
        }}
      />

    </Box>
  );
}